import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { fmtDateTime, fmtNum, fmtChange } from "@/lib/format";
import { TrendingUp, TrendingDown, Search, Download, Filter, FileText, Calendar as CalIcon, Info, ChevronDown, X } from "lucide-react";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import LastUpdateChip from "@/components/LastUpdateChip";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const CHANGE_TOOLTIP_TEXT = "Differenza tra il prezzo di oggi (Ask o Last, quando disponibile) e la chiusura del giorno di trading precedente (Close).";

/**
 * MultiFilter — dropdown compatto con checkbox multiple.
 * Se `values` è vuoto → nessun filtro applicato (mostra "Tutti").
 * Se ha selezioni → mostra il conteggio nel bottone.
 */
function MultiFilter({ label, options, values, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (val) => {
    if (values.includes(val)) onChange(values.filter((v) => v !== val));
    else onChange([...values, val]);
  };
  const clear = (e) => { e.stopPropagation(); onChange([]); };
  const summary = values.length === 0 ? "Tutti" : (values.length === 1 ? String(values[0]) : `${values.length} selezionati`);
  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm bg-white outline-none ${open ? "border-blue-500" : "border-slate-200"} hover:border-blue-500`}
      >
        <span className="text-slate-500">{label}:</span>
        <span className={`font-semibold ${values.length ? "text-blue-600" : "text-slate-700"}`}>{summary}</span>
        {values.length > 0 && (
          <span onClick={clear} role="button" aria-label={`Pulisci ${label}`} className="ml-1 text-slate-400 hover:text-rose-500" data-testid={`${testid}-clear`}>
            <X size={12} />
          </span>
        )}
        <ChevronDown size={14} className="text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 min-w-[180px] max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg p-2" data-testid={`${testid}-menu`}>
          {options.length === 0 && <div className="text-xs text-slate-400 px-2 py-1">Nessuna opzione</div>}
          {options.map((opt) => {
            const val = typeof opt === "object" ? opt.value : opt;
            const lbl = typeof opt === "object" ? opt.label : opt;
            const checked = values.includes(val);
            return (
              <label key={String(val)} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm">
                <input type="checkbox" checked={checked} onChange={() => toggle(val)} className="rounded border-slate-300" />
                <span className={checked ? "text-blue-600 font-semibold" : "text-slate-700"}>{lbl}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Prezzi() {
  const [prices, setPrices] = useState([]);
  const [sheets, setSheets] = useState([]);       // multi: array di source_sheet
  const [periodTypes, setPeriodTypes] = useState([]); // multi
  const [selYears, setSelYears] = useState([]);   // multi: array di period_year come string
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("contract_name");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [unitGas, setUnitGas] = useState("mwh"); // mwh | smc — solo per TTF/PSV
  const [uploadId, setUploadId] = useState("");
  const [historyDates, setHistoryDates] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyFrom, setHistoryFrom] = useState("");
  const [historyTo, setHistoryTo] = useState("");
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyPreview, setHistoryPreview] = useState(null);
  const [historyPreviewBusy, setHistoryPreviewBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get("/prices/history-dates").then((r) => setHistoryDates(r.data)).catch(() => {});
  }, []);

  // Live preview: quando cambia il range nel modal, mostra le date che verranno esportate
  useEffect(() => {
    if (!showHistoryModal || !historyFrom || !historyTo || historyFrom > historyTo) {
      setHistoryPreview(null);
      return;
    }
    let cancelled = false;
    setHistoryPreviewBusy(true);
    const timer = setTimeout(() => {
      api
        .get(`/prices/history-dates-in-range?date_from=${historyFrom}&date_to=${historyTo}`)
        .then((r) => { if (!cancelled) setHistoryPreview(r.data); })
        .catch(() => { if (!cancelled) setHistoryPreview({ count: 0, dates: [] }); })
        .finally(() => { if (!cancelled) setHistoryPreviewBusy(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showHistoryModal, historyFrom, historyTo]);

  const SMC_FACTOR = 1.05833;
  const conv = (v, sheet) => {
    if (v == null) return null;
    if (unitGas === "smc" && (sheet === "TTF" || sheet === "PSV")) return v * SMC_FACTOR;
    return v;
  };
  const unitLabel = (sheet) => (unitGas === "smc" && (sheet === "TTF" || sheet === "PSV")) ? "c€/Smc" : "€/MWh";

  const load = useCallback(async (opts = {}) => {
    if (opts.silent) setRefreshing(true);
    else setLoading(true);
    try {
      const params = uploadId ? { upload_id: uploadId } : {};
      const { data } = await api.get("/prices", { params });
      setPrices(data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [uploadId]);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh 60s attivo SOLO quando stiamo guardando dati correnti (non uno snapshot storico)
  useAutoRefresh(() => load({ silent: true }), 60_000, !uploadId);

  const lastPublishedAt = useMemo(() => {
    let max = "";
    for (const p of prices) {
      const ts = p?.imported_at || p?.update_time;
      if (ts && ts > max) max = ts;
    }
    return max || null;
  }, [prices]);

  const downloadPdf = async () => {
    try {
      const resp = await api.get("/prices/report/pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Adybo_Prezzi_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("PDF download failed", e);
      alert("Errore nel download del PDF");
    }
  };

  const years = useMemo(() => {
    const s = new Set(prices.map((p) => p.period_year).filter(Boolean));
    return Array.from(s).sort();
  }, [prices]);

  const filtered = useMemo(() => {
    let arr = prices;
    if (sheets.length) arr = arr.filter((p) => sheets.includes(p.source_sheet));
    if (periodTypes.length) arr = arr.filter((p) => periodTypes.includes(p.period_type));
    if (selYears.length) arr = arr.filter((p) => selYears.includes(String(p.period_year)));
    if (q) {
      const ql = q.toLowerCase();
      arr = arr.filter((p) =>
        (p.product || "").toLowerCase().includes(ql) ||
        (p.contract_name || "").toLowerCase().includes(ql)
      );
    }
    return arr.slice().sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [prices, sheets, periodTypes, selYears, q, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const exportHistory = async () => {
    if (!historyFrom || !historyTo) return;
    setHistoryBusy(true);
    try {
      const params = new URLSearchParams({ date_from: historyFrom, date_to: historyTo });
      if (sheets.length === 1) params.set("source_sheet", sheets[0]);
      if (unitGas) params.set("unit_gas", unitGas);
      const res = await api.get(`/prices/export-history.xlsx?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `adybo_prezzi_storico_${historyFrom}_${historyTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowHistoryModal(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("export history error", e);
    } finally {
      setHistoryBusy(false);
    }
  };

  const exportXLSX = async () => {
    try {
      const params = new URLSearchParams();
      if (sheets.length) params.set("source_sheet", sheets.join(","));
      if (periodTypes.length) params.set("period_type", periodTypes.join(","));
      if (selYears.length) params.set("period_year", selYears.join(","));
      if (q) params.set("q", q);
      if (uploadId) params.set("upload_id", uploadId);
      if (unitGas) params.set("unit_gas", unitGas);
      const res = await api.get(`/prices/export.xlsx?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `adybo_prezzi_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("export error", e);
    }
  };

  return (
    <div className="space-y-5">
      {showHistoryModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 overflow-y-auto" onClick={() => !historyBusy && setShowHistoryModal(false)} data-testid="history-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-display text-xl font-bold text-slate-900">Esporta storico prezzi</h3>
              <p className="text-sm text-slate-500 mt-1">Seleziona il periodo. Per ogni giorno verrà esportato SOLO l'ultimo caricamento pubblicato.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Da</label>
                <input type="date" value={historyFrom} onChange={(e) => setHistoryFrom(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none text-sm" data-testid="history-from" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">A</label>
                <input type="date" value={historyTo} onChange={(e) => setHistoryTo(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none text-sm" data-testid="history-to" />
              </div>
            </div>
            {sheets.length === 1 && (
              <div className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                Filtro foglio attivo: <span className="font-semibold text-blue-700">{sheets[0]}</span> (rimuovi il filtro per esportare tutti i mercati)
              </div>
            )}
            {sheets.length > 1 && (
              <div className="text-xs text-slate-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                Filtro foglio multiplo ({sheets.join(", ")}) — l'export storico include SOLO tutti i mercati; usa un singolo foglio per limitare l'export.
              </div>
            )}
            {historyFrom && historyTo && historyFrom > historyTo && (
              <div className="text-xs text-red-600">La data di inizio deve essere precedente alla data di fine.</div>
            )}
            {/* Live preview delle date che verranno esportate */}
            {historyFrom && historyTo && historyFrom <= historyTo && (
              <div className="border border-slate-200 rounded-lg bg-slate-50" data-testid="history-preview">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white rounded-t-lg">
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Anteprima date incluse</div>
                  <div className="text-xs text-slate-500" data-testid="history-preview-count">
                    {historyPreviewBusy
                      ? "…"
                      : historyPreview
                        ? `${historyPreview.count} giorn${historyPreview.count === 1 ? "o" : "i"}`
                        : "0"}
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto text-xs px-3 py-2 space-y-0.5">
                  {historyPreviewBusy && <div className="text-slate-400 italic">Caricamento…</div>}
                  {!historyPreviewBusy && historyPreview && historyPreview.count === 0 && (
                    <div className="text-slate-500 italic">Nessun upload pubblicato in questo intervallo.</div>
                  )}
                  {!historyPreviewBusy && historyPreview && historyPreview.dates.slice(0, 30).map((d) => (
                    <div key={d.date} className="flex items-center justify-between py-0.5 text-slate-700">
                      <span className="font-medium tabular-nums">
                        {new Date(d.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                      </span>
                      <span className="text-slate-400">{d.time}</span>
                    </div>
                  ))}
                  {!historyPreviewBusy && historyPreview && historyPreview.count > 30 && (
                    <div className="text-slate-400 italic pt-1 border-t border-slate-200">
                      … e altre {historyPreview.count - 30} date
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowHistoryModal(false)} disabled={historyBusy} className="flex-1 px-4 py-2 rounded-lg border border-slate-200 hover:border-slate-400 text-sm font-semibold disabled:opacity-40">Annulla</button>
              <button
                onClick={exportHistory}
                disabled={historyBusy || !historyFrom || !historyTo || historyFrom > historyTo}
                className="flex-1 btn-brand text-sm flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="history-download"
              >
                {historyBusy ? "Generazione…" : <><Download size={14} /> Scarica Excel</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Prezzi di tutti i periodi</h1>
          <p className="text-sm text-slate-500 mt-1">Curva forward completa: TTF, PSV, Power e CO2.</p>
          <div className="mt-2">
            <LastUpdateChip
              ts={uploadId ? (historyDates.find((h) => h.id === uploadId)?.published_at || lastPublishedAt) : lastPublishedAt}
              autoRefreshing={refreshing}
              testid="prezzi-last-update"
            />
            {uploadId && (
              <span className="ml-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                Vista storica — auto-refresh disattivo
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <button onClick={exportXLSX} className="text-sm border border-slate-200 hover:border-blue-500 hover:text-blue-600 bg-white rounded-lg px-4 py-2 font-semibold flex items-center gap-2" data-testid="prezzi-export-xlsx">
            <Download size={15} /> Esporta Excel
          </button>
          <button onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); setShowHistoryModal(true); }} className="text-sm border border-slate-200 hover:border-purple-500 hover:text-purple-600 bg-white rounded-lg px-4 py-2 font-semibold flex items-center gap-2" data-testid="prezzi-export-history">
            <CalIcon size={15} /> Esporta Excel dati storici
          </button>
        </div>
      </div>

      <div className="card-elevated p-4 flex flex-wrap gap-2 items-center">
        <Filter size={16} className="text-slate-400 ml-1" />
        <MultiFilter
          label="Foglio"
          options={[
            { value: "TTF", label: "TTF" },
            { value: "PSV", label: "PSV" },
            { value: "Power", label: "Power" },
            { value: "CO2", label: "CO2" },
          ]}
          values={sheets}
          onChange={setSheets}
          testid="filter-sheet"
        />
        <MultiFilter
          label="Periodo"
          options={[
            { value: "Spot", label: "Spot" },
            { value: "Month", label: "Month" },
            { value: "Quarter", label: "Quarter" },
            { value: "Season", label: "Season" },
            { value: "MidDec", label: "MidDec" },
            { value: "Calendar", label: "Calendar" },
            { value: "Other", label: "Other" },
          ]}
          values={periodTypes}
          onChange={setPeriodTypes}
          testid="filter-period"
        />
        <MultiFilter
          label="Anno"
          options={years.map((y) => ({ value: String(y), label: String(y) }))}
          values={selYears}
          onChange={setSelYears}
          testid="filter-year"
        />
        <div className="flex items-center gap-1 border border-slate-200 rounded-lg pl-2 pr-1 py-1 bg-white" data-testid="filter-upload">
          <CalIcon size={14} className="text-slate-400" />
          <select
            value={uploadId}
            onChange={(e) => setUploadId(e.target.value)}
            className="text-xs outline-none bg-transparent min-w-[220px] max-w-[320px]"
            data-testid="filter-upload-select"
            title={uploadId ? (historyDates.find((h) => h.id === uploadId)?.label || "") : "Seleziona un caricamento specifico"}
          >
            <option value="">Caricamento: ultimo pubblicato</option>
            {historyDates.map((h) => (
              <option key={h.id} value={h.id}>
                {h.date && h.time ? `${h.date.split("-").reverse().join("/")} · ${h.time}` : (h.label || h.date)}
                {h.filename ? ` — ${h.filename}` : ""}
              </option>
            ))}
          </select>
          {uploadId && (
            <button onClick={() => setUploadId("")} className="text-xs text-slate-400 hover:text-red-500 px-1" title="Reset" data-testid="filter-upload-clear">✕</button>
          )}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto o contratto…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="filter-search" />
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="unit-toggle">
          <button onClick={() => setUnitGas("mwh")} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${unitGas==="mwh"?"bg-white shadow text-blue-600":"text-slate-500"}`} data-testid="unit-mwh">€/MWh</button>
          <button onClick={() => setUnitGas("smc")} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${unitGas==="smc"?"bg-white shadow text-blue-600":"text-slate-500"}`} data-testid="unit-smc">c€/Smc</button>
        </div>
        <div className="text-xs text-slate-500 ml-auto">Risultati: <span className="font-semibold text-slate-800">{filtered.length}</span></div>
      </div>

      <div className="text-[11px] text-slate-500 -mt-2">
        Unità di misura: <span className="font-semibold">€/MWh</span> per Power, <span className="font-semibold">€/tCO2</span> per CO2. Per Gas TTF/PSV puoi visualizzare in €/MWh oppure in c€/Smc (fattore 1,05833). Il fattore c€/Smc non si applica a Power/CO2.
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table w-full min-w-[700px]">
            <thead>
              <tr>
                {[
                  ["commodity", "Commodity"],
                  ["contract_name", "Contract"],
                  ["period_type", "Periodo"],
                  ["previous_close", "Close"],
                  ["ask", "Ask"],
                  ["bid", "Bid"],
                  ["last_price", "Last"],
                  ["change_value", "Change"],
                  ["update_time", "UpdateTime"],
                ].map(([k, l]) => (
                  <th key={k} onClick={() => toggleSort(k)} className="cursor-pointer hover:text-blue-600">
                    <span className="inline-flex items-center gap-1">
                      {l}
                      {k === "change_value" && (
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <span className="text-slate-400 hover:text-blue-600 cursor-help" data-testid="change-tooltip-trigger">
                                <Info size={12} />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs font-normal">
                              {CHANGE_TOOLTIP_TEXT}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center text-slate-400 py-8">Caricamento…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-8">Nessun dato</td></tr>}
              {filtered.map((p) => {
                const ch = p.change_value;
                const isGasSmc = unitGas === "smc" && (p.source_sheet === "TTF" || p.source_sheet === "PSV");
                const dig = isGasSmc ? 4 : 3;
                return (
                  <tr key={p.id} data-testid={`price-row-${p.id}`}>
                    <td>{p.commodity}</td>
                    <td className="!font-sans !font-semibold">{p.contract_name}</td>
                    <td>{p.period_type}</td>
                    <td>{fmtNum(conv(p.previous_close, p.source_sheet), dig)}</td>
                    <td>{fmtNum(conv(p.ask, p.source_sheet), dig)}</td>
                    <td>{fmtNum(conv(p.bid, p.source_sheet), dig)}</td>
                    <td className="font-semibold">{fmtNum(conv(p.last_price, p.source_sheet), dig)}</td>
                    <td>
                      {ch != null && (
                        <span className={ch > 0 ? "badge-neg" : ch < 0 ? "badge-pos" : "badge-neutral"}>
                          {ch > 0 ? <TrendingUp size={11} /> : ch < 0 ? <TrendingDown size={11} /> : null}
                          {fmtChange(conv(ch, p.source_sheet))}
                        </span>
                      )}
                    </td>
                    <td className="text-[11px] text-slate-500">{fmtDateTime(p.update_time)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
